import React, { useEffect, useState, forwardRef, useImperativeHandle, useCallback } from "react";
import inputStyle from "../ImageUploader/imageUploader.module.css";
import { faClose, faStar, faCloudUploadAlt, faImage, faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDropzone } from "react-dropzone";

import arrayMove from "array-move";

import { Draggable } from "react-drag-reorder";
import axios from "axios";

const BannerPreview = ({ src, onDelete, primary, index }) => {
  return (
    <div className={inputStyle.bannerCard}>
      <div className={inputStyle.bannerImg}>
        <img src={src.preview} alt={`Banner ${index + 1}`} />
      </div>
      <div className={inputStyle.bannerOverlay}>
        {primary && (
          <span className={inputStyle.primaryBadge}>
            <FontAwesomeIcon icon={faStar} /> Primary
          </span>
        )}
        <span className={inputStyle.bannerIndex}>#{index + 1}</span>
        <button
          type="button"
          className={inputStyle.deleteBtn}
          onClick={(e) => { e.stopPropagation(); onDelete(src); }}
          aria-label="Remove banner"
        >
          <FontAwesomeIcon icon={faClose} />
        </button>
      </div>
    </div>
  );
};

const RenderPhotos = ({ source, setSource, onDelete }) => {
  const [dragKey, setDragKey] = useState(1);
  useEffect(() => {
    setDragKey((prev) => prev + 1);
  }, [source]);
  const handlePosChange = (currentPos, newPos) => {
    setSource(arrayMove(source, currentPos, newPos));
  };
  if (source.length === 0) return null;
  return (
    <div className={inputStyle.bannerList}>
      <Draggable key={dragKey} onPosChange={handlePosChange} className={inputStyle.dragColumn}>
        {source.map((photo, index) => (
          <div key={index} className={inputStyle.bannerWrap}>
            <BannerPreview src={photo} onDelete={onDelete} primary={index === 0} index={index} />
          </div>
        ))}
      </Draggable>
    </div>
  );
};

const ImageUploader = forwardRef(({ selectedFiles = [], numberOfImages = 10, setSelectedFiles }, ref) => {
  const [noOfImageError, setNoOfImageError] = useState(false);
  const [sizeOfImageError, setSizeOfImageError] = useState(false);

  const [deletedImages, setDeletedImages] = useState([]);

  const [isDragActive, setIsDragActive] = useState(false);

  const onDrop = useCallback((acceptedFiles, fileRejections) => {
    setIsDragActive(false);
    setSizeOfImageError(false);
    setNoOfImageError(false);
    fileRejections.forEach((file) => {
      file.errors.forEach((err) => {
        if (err.code === "file-too-large") {
          setSizeOfImageError(true);
        }
      });
    });
    const filesArray = acceptedFiles.map((file) => ({ preview: URL.createObjectURL(file), data: file, type: "file" }));
    if (selectedFiles.length + filesArray.length <= numberOfImages) {
      setSelectedFiles((prevImage) => [...prevImage, ...filesArray]);
    } else {
      const remaining = numberOfImages - selectedFiles.length;
      if (remaining > 0) {
        setSelectedFiles((prevImage) => [...prevImage, ...filesArray.slice(0, remaining)]);
      }
      setNoOfImageError(true);
    }
  }, [selectedFiles, numberOfImages, setSelectedFiles]);

  const { getRootProps, getInputProps, open } = useDropzone({
    accept: {
      "image/png": [".png"],
      "image/jpeg": [".jpg", ".jpeg"],
    },
    onDrop,
    onDragEnter: () => setIsDragActive(true),
    onDragLeave: () => setIsDragActive(false),
    maxSize: 10485760,
    noClick: true,
  });

  const onDelete = (photo) => {
    setSelectedFiles((prevImage) => prevImage.filter((img) => img !== photo));
    setNoOfImageError(false);
    setSizeOfImageError(false);
  };

  useImperativeHandle(ref, () => ({
    uploadingImages: () => {
      return new Promise((resolve, reject) => {
        if (selectedFiles.length > 0) {
          const formData = new FormData();
          selectedFiles.map((file) => {
            if (file.type === "file") {
              formData.append("files", file.data, file.data.name);
            }
          });

          axios
            .post(`${process.env.REACT_APP_URL_IMAGE_CDN}/images`, formData, { headers: { Accept: "application/json", "content-type": "multipart/form-data" } })
            .then(({ data }) => {
              var filesArray = [];
              var counter = 0;
              selectedFiles.map((file) => {
                if (file.type === "url") {
                  filesArray.push(file.preview);
                } else {
                  filesArray.push(data.files[counter]);
                  counter = counter + 1;
                }
              });

              resolve(filesArray);
            })
            .catch((err) => {
              reject(err);
            });
        } else {
        }
      });
    },
    deleteImages: () => {
      return new Promise((resolve, reject) => {
        if (deletedImages.length > 0) {
          deletedImages.forEach((photo) => {
            axios
              .delete(photo.preview)
              .then((res) => {})
              .catch((err) => {});
          });
        }
        resolve(true);
      });
    },
  }));

  const showDropzone = selectedFiles.length < numberOfImages;
  const remaining = numberOfImages - selectedFiles.length;

  return (
    <div className={inputStyle.uploaderSection}>
      <input {...getInputProps()} />
      <RenderPhotos source={selectedFiles} setSource={setSelectedFiles} onDelete={onDelete} />
      {showDropzone && (
        <div
          {...getRootProps()}
          className={`${inputStyle.dropzone} ${isDragActive ? inputStyle.dropzoneActive : ""}`}
        >
          <div className={inputStyle.dropzoneContent}>
            <FontAwesomeIcon icon={faCloudUploadAlt} className={inputStyle.dropzoneIcon} />
            <p className={inputStyle.dropzoneText}>
              Drag &amp; drop images here, or{" "}
              <button type="button" className={inputStyle.browseBtn} onClick={open}>
                browse
              </button>
            </p>
            <span className={inputStyle.dropzoneHint}>JPEG, JPG, PNG · max 10 MB per file</span>
            <span className={inputStyle.dropzoneCount}>
              {selectedFiles.length} of {numberOfImages} images added
              {remaining > 0 && ` · ${remaining} remaining`}
            </span>
          </div>
        </div>
      )}
      {(sizeOfImageError || noOfImageError) && (
        <div className={inputStyle.errorBar} role="alert">
          <FontAwesomeIcon icon={faExclamationCircle} />
          {sizeOfImageError && <span>Image exceeds the 10 MB limit.</span>}
          {noOfImageError && <span>Maximum {numberOfImages} images allowed.</span>}
        </div>
      )}
      {showDropzone && (
        <button type="button" className={inputStyle.addBtn} onClick={open}>
          <FontAwesomeIcon icon={faImage} />
          Add Images
        </button>
      )}
    </div>
  );
});

export default ImageUploader;
